// Conversation storage of the hosted chat page — the same model as the website
// widget (ultimo-bots-widget/src/index.js): session id, live token, chat history
// and the last agent message id are shared by every tab (localStorage), so a
// second tab continues the chat. A conversation ends 30 minutes after its last
// activity, unless the team replied to it since: then it continues on the next
// visit, for up to 30 days. With localStorage blocked it stays per tab.

export const CONV_IDLE_MS = 30 * 60 * 1000;
export const CONV_FOLLOWUP_MS = 30 * 24 * 60 * 60 * 1000;

export function convKeys(botId) {
  return {
    session: `sessionId-${botId}`,
    history: `chat-history-${botId}`,
    token: `ultimo_live_session_token-${botId}`,
    agentMsg: `ultimo_live_last_agent_msg-${botId}`,
    active: `ultimo_conv_active-${botId}`,
    // Proves on a later visit that this browser owns the chat (/api/live/resume).
    resume: `ultimo_live_resume-${botId}`,
  };
}

let store = null;
let shared = false; // true when the tabs share localStorage

function convStore() {
  if (!store) {
    try {
      localStorage.setItem('ultimo_conv_probe', '1');
      localStorage.removeItem('ultimo_conv_probe');
      store = localStorage;
      shared = true;
    } catch {
      store = sessionStorage;
    }
  }
  return store;
}

export function convShared() {
  convStore();
  return shared;
}

export function convGet(key) {
  try { return convStore().getItem(key); } catch { return null; }
}

export function convSet(key, value) {
  try { convStore().setItem(key, value); } catch { /* quota or blocked */ }
}

export function convRemove(key) {
  try { convStore().removeItem(key); } catch { /* blocked */ }
}

export function convTouch(botId) {
  convSet(convKeys(botId).active, String(Date.now()));
}

// Once per page and bot (React runs effects twice in development): expire an idle
// conversation, keep it "resume only" when the team may have replied, and take over
// a chat that the previous page version kept in sessionStorage — once, removing
// those keys so a cleared or expired chat never comes back from them.
const started = {};

export function startConversationStorage(botId) {
  if (started[botId]) return started[botId];
  const result = { resumeOnly: false };
  started[botId] = result;
  if (!convShared()) return result;
  const k = convKeys(botId);
  const idle = Date.now() - (Number(convGet(k.active)) || 0);
  if (idle > CONV_IDLE_MS) {
    if (idle <= CONV_FOLLOWUP_MS && convGet(k.resume) && convGet(k.history)) {
      result.resumeOnly = true; // decided by the chat page once the replies are known
    } else {
      [k.session, k.history, k.token, k.agentMsg, k.resume].forEach(convRemove);
    }
  }
  try {
    const legacy = [k.session, k.history, k.token].map((key) => [key, sessionStorage.getItem(key)]);
    if (!convGet(k.session) && legacy[1][1]) {
      legacy.forEach(([key, value]) => { if (value) convSet(key, value); });
      convTouch(botId);
    }
    legacy.forEach(([key]) => sessionStorage.removeItem(key));
  } catch { /* sessionStorage blocked */ }
  return result;
}
